import { useState } from 'react';
import { IonAlert, useIonToast } from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import { REMOVE_PLAYER_FROM_MARKET } from '../../../graphql/mutations/marketEntry';
import { GET_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/activeMarketEntries';
import { GET_MY_MARKET_BIDS } from '../../../graphql/queries/myMarketBids';
import { GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/currentFantasyLeagueMemberActiveMarketEntries';
import type {
  RemovePlayerFromMarketMutation,
  RemovePlayerFromMarketMutationVariables,
} from '../../../types/graphql-generated';

/**
 * Encapsulates the "Retirar del mercado" flow so it can be reused from any tab
 * that lets a user withdraw one of their own players from the market.
 *
 * It owns the confirmation alert, the mutation and the error toast. Callers
 * trigger the flow with `requestRemoveFromMarket(marketEntryId)` and must render
 * the returned `removeFromMarketAlert` element.
 */
export const useRemovePlayerFromMarket = (leagueId: string) => {
  const [presentToast] = useIonToast();
  const [marketEntryId, setMarketEntryId] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const [removePlayerFromMarket] = useMutation<
    RemovePlayerFromMarketMutation,
    RemovePlayerFromMarketMutationVariables
  >(REMOVE_PLAYER_FROM_MARKET, {
    refetchQueries: [
      {
        query: GET_ACTIVE_MARKET_ENTRIES,
        variables: { fantasyLeagueId: leagueId },
      },
      {
        query: GET_MY_MARKET_BIDS,
        variables: { fantasyLeagueId: leagueId },
      },
      {
        query: GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES,
        variables: { fantasyLeagueId: leagueId },
      },
    ],
  });

  const reset = () => {
    setIsAlertOpen(false);
    setMarketEntryId(null);
  };

  const requestRemoveFromMarket = (entryId: string) => {
    setMarketEntryId(entryId);
    setIsAlertOpen(true);
  };

  const confirmRemoveFromMarket = async () => {
    if (!marketEntryId) return;

    try {
      const result = await removePlayerFromMarket({
        variables: { marketEntryId },
      });

      if (
        result.data?.removePlayerFromMarket?.errors &&
        result.data.removePlayerFromMarket.errors.length > 0
      ) {
        await presentToast({
          message: result.data.removePlayerFromMarket.errors.join(', '),
          duration: 3000,
          color: 'danger',
        });
      }
    } catch (error) {
      console.error('Error removing player from market:', error);
      await presentToast({
        message: 'Error al retirar el jugador del mercado',
        duration: 3000,
        color: 'danger',
      });
    }

    reset();
  };

  const removeFromMarketAlert = (
    <IonAlert
      isOpen={isAlertOpen}
      header="Retirar del mercado"
      message="¿Estás seguro de que quieres retirar a tu jugador del mercado? Esto rechazará automáticamente todas las ofertas que hubiera abiertas por el jugador."
      buttons={[
        {
          text: 'Mantener',
          role: 'cancel',
          handler: reset,
        },
        {
          text: 'Retirar',
          role: 'confirm',
          handler: confirmRemoveFromMarket,
        },
      ]}
      onDidDismiss={reset}
    />
  );

  return { requestRemoveFromMarket, removeFromMarketAlert };
};
